<template>
    <b-container id="portal" class="portal portal-submission mt-5" v-if="portal && submission">
        <!-- SINGLE PAGE PORTAL -->
        <template v-if="portal.singlePage">
            <b-card>
                <b-form @submit.prevent="onSubmitAll()">
                    <div v-for="(step, index) in orderedSteps" v-bind:key="step.position">
                        <b-card-title class="h1">{{ step.name }}</b-card-title>
                        <step v-if="step.type === 'SUBMISSION'" :portal="portal" :step="step"
                              :stepValues="submission.submissionValues" :stepIndex="index"/>
                        <step v-if="step.type === 'SUBMITTER' || step.type === 'CONTACT'" :portal="portal" :step="step"
                              :stepValues="submission.submitterValues[step.position]" :stepIndex="index"/>
                        <step-summary v-if="step.type === 'SUMMARY'" :portal="portal" :step="step" :stepIndex="index"
                                     :submission="submission"/>
                        <b-row v-if="portal.captchaCheck && index === orderedSteps.length - 1" class="mt-3">
                            <b-col>
                                <vue-recaptcha id="recaptcha" @verify="onVerify" :sitekey="captchaSiteKey"
                                               style="width: fit-content;">
                                </vue-recaptcha>
                                <b-popover :show.sync="captchaError" target="recaptcha" placement="bottom" variant="danger">
                                    This field is required.
                                </b-popover>
                            </b-col>
                        </b-row>
                    </div>
                    <b-row class="mt-3">
                        <b-col class="text-center">
                            <b-alert :show="submitErrorState" variant="danger" dismissible fade>
                                <span v-for="(err, index) in submitErrors" :key="index">{{ err }}<br></span>
                            </b-alert>
                            <b-button type="submit" variant="primary" class="submit-btn text-uppercase" :disabled="submitting">
                                <b-spinner v-if="submitting" small type="grow"></b-spinner>
                                {{ portal.submitSubmissionButton ? portal.submitSubmissionButton : 'Submit' }}
                            </b-button>
                        </b-col>
                    </b-row>
                </b-form>
                <b-overlay :show="submitting" variant="light" opacity="0.85" blur="2px" no-wrap>
                </b-overlay>
            </b-card>
        </template>
        <!-- DEFAULT MULTIPLE PAGES PORTAL -->
        <template v-else>
            <b-card v-for="(step, index) in orderedSteps" v-bind:key="step.position"
                    v-show="index === submission.currentStep">
                <b-card-title class="h1">{{ step.name }}</b-card-title>
                <b-form :id="'step-'+index" :name="'step-'+index" @submit.prevent="onSubmitStep(index)">
                    <step v-if="step.type === 'SUBMISSION'" :portal="portal" :step="step"
                          :stepValues="submission.submissionValues" :stepIndex="index"/>
                    <step v-if="step.type === 'SUBMITTER' || step.type === 'CONTACT'" :portal="portal" :step="step"
                          :stepValues="submission.submitterValues[step.position]" :stepIndex="index"/>
                    <step-summary v-if="step.type === 'SUMMARY'" :portal="portal" :step="step" :stepIndex="index"
                                 :submission="submission"/>

                    <b-row v-if="portal.captchaCheck && index === orderedSteps.length - 1" class="mt-3">
                        <b-col>
                            <vue-recaptcha id="recaptcha" @verify="onVerify" :sitekey="captchaSiteKey"
                                           style="width: fit-content;">
                            </vue-recaptcha>
                            <b-popover :show.sync="captchaError" target="recaptcha" placement="bottom" variant="danger">
                                This field is required.
                            </b-popover>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col class="text-center">
                            <b-alert :show="submitErrorState" variant="danger" dismissible fade>
                                <span v-for="(err, index) in submitErrors" :key="index">{{ err }}<br></span>
                            </b-alert>
                            <b-button v-if="index !== 0" @click="previousStep" variant="primary"
                                      class="back-btn mr-2 text-uppercase">Back
                            </b-button>
                            <b-button v-if="index !== orderedSteps.length - 1" type="submit" variant="primary"
                                      class="continue-btn text-uppercase">Continue
                            </b-button>
                            <b-button v-if="index === orderedSteps.length - 1" type="submit" variant="primary"
                                      class="submit-btn text-uppercase" :disabled="submitting">
                                <b-spinner v-if="submitting" small type="grow"></b-spinner>
                                {{ portal.submitSubmissionButton ? portal.submitSubmissionButton : 'Submit' }}
                            </b-button>
                        </b-col>
                    </b-row>
                </b-form>
                <b-overlay :show="submitting" variant="light" opacity="0.85" blur="2px" no-wrap>
                </b-overlay>
            </b-card>
        </template>

        <!-- Modal -->
        <b-modal :id="htmlModal.id" :title="htmlModal.title" size="lg" @hide="resetHtmlModal" centered scrollable
                 hide-footer header-bg-variant="light">
            <div v-html="htmlModal.content"></div>
        </b-modal>

    </b-container>
</template>

<script>
    import _ from 'lodash';
    import VueRecaptcha from 'vue-recaptcha';
    import SubmissionService from "@/services/SubmissionService";
    import Step from "@/components/portal/submission/Step";
    import StepSummary from "@/components/portal/submission/StepSummary";

    export default {
        name: "Portal",
        components: {
            Step,
            StepSummary,
            VueRecaptcha
        },
        props: {
            portal: Object,
            published: Boolean
        },
        data() {
            return {
                submission: null,
                files: {"documents" : []},
                filesState: null,
                filesErrors: [],
                maxFileSize: this.$appSettings.maxFileSize,
                maxRequestSize: this.$appSettings.maxRequestSize,
                submitting: false,
                // Captcha
                captchaSiteKey: this.$appSettings.captchaSiteKey,
                robot: false,
                captchaError: false,
                htmlModal: {
                    id: 'html-modal',
                    title: '',
                    content: ''
                },
                submitErrorState: false,
                submitErrors: []
            }
        },
        mounted() {
            if (this.portal) {
                this.getSubmission(this.portal);
            }
        },
        methods: {
            getSubmission(portal) {
                SubmissionService.initSubmission(portal, this.$route.query)
                    .then(response => (this.submission = response.data))
                    .catch(error => {
                        this.submitting = false;
                        this.$router.push({
                            name: this.published ? 'PortalError' : 'DraftError', params: {
                                portal: this.portal,
                                published: this.published,
                                errorMessage: error
                            }
                        })
                    });
            },
            previousStep() {
                this.submission.currentStep--;
                let currentStep = this.orderedSteps[this.submission.currentStep];
                if (currentStep.condition) {
                    if (!this.isConditionValid(currentStep)) {
                        this.previousStep();
                    }
                }
            },
            nextStep() {
                this.submission.currentStep++;
                let currentStep = this.orderedSteps[this.submission.currentStep];
                if (currentStep.condition) {
                    if (!this.isConditionValid(currentStep)) {
                        this.nextStep();
                    }
                }
            },
            onSubmitStep(index) {
                this.filesState = null;
                this.filesErrors = [];
                if (!this.checkDocumentSize()) {
                    return;
                }
                this.$validator.validateAll('step-' + index).then(result => {
                    if (!result) {
                        return;
                    }
                    if (index === this.orderedSteps.length - 1) {
                        return this.submit();
                    } else {
                        this.nextStep()
                    }
                });
            },
            onSubmitAll() {
                this.filesState = null;
                this.filesErrors = [];
                if (!this.checkDocumentSize()) {
                    return;
                }
                this.$validator.validateAll().then(result => {
                    if (!result) {
                        return;
                    }
                    return this.submit();
                });

            },
            submit() {
                if (!this.portal.captchaCheck || this.robot) {
                    this.submitting = true;
                    this.submitErrorState = false;
                    this.submitErrors = [];
                    SubmissionService.submit(this.portal.name, this.submission, this.files)
                        .then(response => {
                            this.submitting = false;
                            let statusCode = response.data;
                            if (statusCode.type === "SUCCESS") {
                                this.$router.push({
                                    name: this.published ? 'PortalConfirmation' : 'DraftConfirmation',
                                    params: {statusCode: statusCode, published: this.published}
                                })
                            } else {
                                if (statusCode.statusCode === "CONFLICT") {
                                    this.submitErrorState = true;
                                    if (statusCode.message) {
                                        this.submitErrors.push(statusCode.message);
                                    } else {
                                        this.submitErrors.push(...statusCode.messages);
                                    }
                                } else {
                                    this.$router.push({
                                        name: this.published ? 'PortalError' : 'DraftError',
                                        params: {
                                            portal: this.portal,
                                            published: this.published,
                                            errorMessage: statusCode.message
                                        }
                                    })
                                }
                            }
                        })
                        .catch(error => {
                            this.submitting = false;
                            let errorData = error.response.data;
                            let status = error.response.status;
                            if (status === 500) {
                                this.submitErrorState = true;
                                this.submitErrors.push(error.response.statusText);
                            } else {
                                let message;
                                if (!this.currentUser) {
                                    message = errorData.code + ' : An error occurred while submitting an opportunity';
                                } else {
                                    message = errorData.message;
                                }
                                this.$router.push({
                                    name: this.published ? 'PortalError' : 'DraftError', params: {
                                        portal: this.portal,
                                        published: this.published,
                                        errorMessage: message
                                    }
                                })
                            }
                        });
                } else if (this.portal.captchaCheck && !this.robot) {
                    this.captchaError = true;
                }
            },
            checkDocumentSize() {
                let maxSize = parseFloat(this.maxRequestSize) * (1024 * 1024);
                let maxFileSize = parseFloat(this.maxFileSize) * (1024 * 1024);
                let filesSize = 0;
                let emptyFile = false;
                let hasWrongExt = false;
                let hasMaxSize = false;
                let wrongName = false;
                let allowedExtensions = this.portal.allowedExtensions.trim().split(",");

                var regex = new RegExp("([\\\\/<>:;,?\"*&|]|((.*[.].*){2,}))");

                Object.keys(this.files).forEach(key => {
                    const files = this.files[key]
                    files.forEach(file => {
                        if (file.size > maxFileSize) {
                            hasMaxSize = true;
                        }
                        filesSize += file.size;
                        let allowedExt = false;
                        allowedExtensions.forEach(ext => {
                            if (file.name.toLowerCase().endsWith(ext.trim().toLowerCase())) {
                                allowedExt = true;
                            }
                        });
                        if (!hasWrongExt) {
                            hasWrongExt = !allowedExt;
                        }
                        if (file.size === 0) {
                            emptyFile = true;
                        }
                        if (regex.test(file.name)) {
                            console.log(file.name)
                            wrongName = true;
                        }
                    });
                })
                if (hasWrongExt) {
                    this.filesState = false;
                    this.filesErrors.push(`Invalid file extension selected, allowed extensions : ${this.portal.allowedExtensions}`);
                }
                if (hasMaxSize) {
                    this.filesState = false;
                    this.filesErrors.push(`File size exceeds maximum limit of ${this.maxFileSize} MB.`);
                }
                if (filesSize > maxSize) {
                    this.filesState = false;
                    this.filesErrors.push(`Total files size exceeds maximum limit of ${this.maxRequestSize} MB.`);
                }
                if (emptyFile) {
                    this.filesState = false;
                    this.filesErrors.push("Empty file selected.");
                }
                if (wrongName) {
                    this.filesState = false;
                    this.filesErrors.push(`File name not valid. It contains one or more following forbidden characters : <>:;,?\\"*&|/\\$%`);
                }
                if (this.filesErrors && this.filesErrors.length > 0) {
                    return false;
                }
                return true;
            },
            onVerify: function (response) {
                if (response) this.robot = true;
            },
            showHtmlModal(title, content) {
                this.htmlModal.title = title;
                this.htmlModal.content = content;
                this.$root.$emit('bv::show::modal', this.htmlModal.id)
            },
            resetHtmlModal() {
                this.htmlModal.title = '';
                this.htmlModal.content = '';
            },
            isConditionValid(nextStep) {
                if (nextStep.condition) {
                    let conditions = nextStep.condition.split(",");
                    // following for loop is for multiple conditions separated by ,
                    for (let condition of conditions) {
                        const field = condition.substring(0, condition.indexOf('='));
                        const conditionValues = condition.substring(condition.indexOf('=') + 1, condition.length);
                        // Retrieve condition field's step
                        const stepPos = Number(field.substring(0, field.indexOf('.')));
                        const fieldName = field.substring(field.indexOf('.') + 1, field.length);
                        const stepIndex = this.portal.steps.findIndex(s => s.position === stepPos);
                        const step = this.portal.steps[stepIndex];
                        const fieldIndex = step.fields.findIndex(item => item.name === fieldName);
                        let stepValues;
                        if (step.type === 'SUBMISSION') {
                            stepValues = this.submission.submissionValues;
                        } else if (step.type === 'SUBMITTER' || step.type === 'CONTACT') {
                            stepValues = this.submission.submitterValues[step.position];
                        }
                        let fieldValue = stepValues[fieldName];

                        if (fieldValue != null) {
                            let hasVal = false;
                            let conditionValuesList = conditionValues.split("|");

                            let isFieldValues = step.fields[fieldIndex].fieldValues && step.fields[fieldIndex].fieldValues[fieldValue];
                            fieldValue = isFieldValues ? step.fields[fieldIndex].fieldValues[fieldValue].label : fieldValue;
                            let fieldValueStr = fieldValue.toString();
                            if (conditionValuesList.includes(fieldValue) || conditionValuesList.includes(fieldValueStr)) {
                                hasVal = true;
                            }
                            if (!hasVal) {
                                return false;
                            }
                        } else {
                            return false;
                        }
                    }
                }
                return true;
            },
        },
        computed: {
            orderedSteps: function () {
                return _.orderBy(this.portal.steps, 'position')
            },
            currentUser() {
                return this.$store.state.auth.user;
            }
        }
    }
</script>

<style lang="scss">
    .fieldDescription {
        font-size: 13px;
        color: #a4a4a4;
    }

    .btn-group-custom {
        display: block;

        .btn {
            margin-top: 5px;
            margin-bottom: 5px;
            min-width: 20%;
        }

        .btn:not(:last-child) {
            //margin-right: 10px;
        }
    }
</style>